import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import StepRow from '../components/StepRow';
import PartnerEntry from '../components/PartnerEntry';

export default function Investors() {
  return (
    <>
      <PageHero
        eyebrow="Investors path"
        title="Growth capital, without giving up the"
        emphasis="company you built."
        lede="A good investor brings more than money — they bring sector reps, hiring networks, and the patience to grow a guard company the right way. The hard part is finding one who actually understands the industry. We did that part for you."
      >
        <a href="https://officercapital.com/register" className="oc-btn oc-btn--primary">List your company</a>
        <Link to="/how-it-works" className="oc-btn oc-btn--link">See how it works →</Link>
      </PageHero>

      {/* WHAT IT'S FOR */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">What investor capital looks like</div>
              <h2 className="oc-h2">
                Funded growth, on <em className="oc-em">your cap table</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              The investors on OfficerCapital aren't looking to take over. Most deals here are minority investments
              and recapitalizations — you keep operating control, you keep your team, you get a partner who's been
              through it before.
            </p>
          </div>

          <div className="oc-grid-3">
            <UseCaseCard
              label="Growth equity"
              body="Capital to expand into new markets, take on bigger contracts, or accelerate hiring. Minority stakes, board involvement light to moderate."
            />
            <UseCaseCard
              label="Recapitalization"
              body="Take chips off the table while staying in the seat. Partial liquidity for owners who aren't ready to fully exit."
            />
            <UseCaseCard
              label="Strategic partnership"
              body="Investors with operating expertise — former guard company owners, security-focused PE firms, family offices with sector portfolios."
            />
          </div>
        </div>
      </section>

      {/* WHO'S ON THE OTHER SIDE */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Who you'll talk to</div>
              <h2 className="oc-h2">
                Investors who already <em className="oc-em">know the industry</em>.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                You won't waste a meeting explaining what billable hours are, how state licensing works, or why your
                margin profile looks the way it does. Every investor here has either operated, advised, or invested in
                guard companies before yours.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <ListPoint title="Sector-specialized" body="Growth equity, family offices, and PE firms with security industry portfolios." />
                <hr className="oc-rule" />
                <ListPoint title="Vetted twice" body="Manually approved by OfficerCapital, plus you approve them again before they see your identity." />
                <hr className="oc-rule" />
                <ListPoint title="Operating experience" body="Many are former owners themselves. The ones that aren't have advisors who are." />
                <hr className="oc-rule" />
                <ListPoint title="Pay to engage" body="Investors pay a subscription and a $500 unlock fee per deal — so they only reach out if they're serious." />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">How investor connections work</div>
              <h2 className="oc-h2">
                Talk to investors before <em className="oc-em">they talk to you</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              You see who wants in before you reveal who you are. That changes the whole tenor of the conversation.
            </p>
          </div>

          <div className="oc-grid-3-wide">
            <StepRow number="01" title="List anonymously">
              Revenue, EBITDA, customer concentration, growth trajectory, what you're looking for in a partner. Your name stays off.
            </StepRow>
            <StepRow number="02" title="Investors request access">
              Vetted investors review your listing and pay to unlock the full file. You see who they are first.
            </StepRow>
            <StepRow number="03" title="Approve and engage">
              Approve only the investors worth a meeting. Compare offers in one place. Negotiate freely — we don't take a cut, ever.
            </StepRow>
          </div>
        </div>
      </section>

      {/* OTHER PATHS */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-eyebrow-gray oc-mb-3">Other paths</div>
          <div className="oc-grid-2">
            <RelatedPathCard
              to="/financing"
              eyebrow="Path 01"
              title="You need financing instead"
              body="Looking for working capital or a credit line? See how lenders connect on OfficerCapital."
            />
            <RelatedPathCard
              to="/sell"
              eyebrow="Path 03"
              title="You're ready to sell outright"
              body="Considering a full sale? See how owners connect with vetted acquirers."
            />
          </div>
        </div>
      </section>

      <PartnerEntry />
    </>
  );
}

function UseCaseCard({ label, body }) {
  return (
    <div className="oc-card">
      <div className="oc-eyebrow-gray oc-mb-2">{label}</div>
      <p className="oc-body-sm" style={{ fontSize: 13, lineHeight: 1.55 }}>{body}</p>
    </div>
  );
}
function ListPoint({ title, body }) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--oc-ink)', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--oc-gray)', lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}
function RelatedPathCard({ to, eyebrow, title, body }) {
  return (
    <Link to={to} className="oc-card oc-card--linked" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <div className="oc-eyebrow oc-mb-2">{eyebrow}</div>
      <h3 className="oc-h3" style={{ marginBottom: 8 }}>{title}</h3>
      <p className="oc-body-sm" style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 12 }}>{body}</p>
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--oc-ink)' }}>Read more →</span>
    </Link>
  );
}
